import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export type PiiRemovalState = {
  viewPiiRequest: boolean;
  piiRemovalRequestId: number;
  piiRemovalFlag: boolean;
  removeCountryFlag: boolean;
  removeReporterFlag: boolean;
  removeAllAttachmentsFlag: boolean;
  removedMessagesId: string[];
  webSocketUrl: string;
  attachmentsEmptyFlag: boolean;
  piiRemovalAccessFlag: boolean;
  piiRemovedDescriptionRanges?: [number, number][];
  messageBoxId: string;
  piiRemovedMessageRanges?: {
    index?: number;
    messageContent?: string;
    messageId: string;
    contentPiiRemovedRanges?: [number, number][];
  }[];
  messages?: {
    index: number;
    createdAt?: string;
    name?: string;
    messageId: string;
    messageContent: string;
    contentPiiRemovedRanges?: [number, number][];
  }[];

  selectedMessage: {
    messageIndex: number;
    messageId: string;
    messageContent: string;
  };
  piiRemovalRequestData: {
    id: number;
    report_reference: string;
    requested_by_id: number;
    requested_by_full_name: string;
    reviewed_by_id: number;
    messages: {
      id: string;
      content_pii_removed_ranges?: [number, number][];
    }[];
    remove_full_name: boolean;
    remove_country: boolean;
    remove_files: boolean;
    description_pii_removed_ranges?: [number, number][];
    status: number;
    status_label: string;
  };
};

const initialState: PiiRemovalState = {
  viewPiiRequest: false,
  piiRemovalRequestData: {
    id: 0,
    report_reference: '',
    requested_by_id: 0,
    requested_by_full_name: '',
    reviewed_by_id: 0,
    messages: [
      {
        id: '',
        content_pii_removed_ranges: [],
      },
    ],
    remove_full_name: false,
    remove_country: false,
    remove_files: false,
    description_pii_removed_ranges: [],
    status: 0,
    status_label: '',
  },
  piiRemovalRequestId: 0,
  selectedMessage: {
    messageIndex: 0,
    messageId: '',
    messageContent: '',
  },
  messages: [],
  piiRemovedDescriptionRanges: [],
  piiRemovalAccessFlag: false,
  piiRemovalFlag: false,
  removeCountryFlag: false,
  removeReporterFlag: false,
  removeAllAttachmentsFlag: false,
  removedMessagesId: [],
  webSocketUrl: '',
  attachmentsEmptyFlag: false,
  messageBoxId: '',
  piiRemovedMessageRanges: [],
};

const piiRemovalSlice = createSlice({
  name: 'piiRemoval',
  initialState,
  reducers: {
    setPiiRemovalRequestData: (
      state,
      action: PayloadAction<PiiRemovalState['piiRemovalRequestData']>,
    ) => {
      state.piiRemovalRequestData = action.payload;
    },
    setPiiRemovalRequestId: (state, action: PayloadAction<number>) => {
      state.piiRemovalRequestId = action.payload;
    },
    setSelectedMessage: (
      state,
      action: PayloadAction<{
        messageIndex: number;
        messageId: string;
        messageContent: string;
      }>,
    ) => {
      state.selectedMessage = action.payload;
    },
    setMessageboxId: (state, action: PayloadAction<string>) => {
      state.messageBoxId = action.payload;
    },
    setWebSocketUrl: (state, action: PayloadAction<string>) => {
      state.webSocketUrl = action.payload;
    },
    setPiiRemovalFlag: (state, action: PayloadAction<boolean>) => {
      state.piiRemovalFlag = action.payload;
    },
    setRemoveCountryFlag: (state, action: PayloadAction<boolean>) => {
      state.removeCountryFlag = action.payload;
    },
    setRemoveReporterFlag: (state, action: PayloadAction<boolean>) => {
      state.removeReporterFlag = action.payload;
    },
    setRemoveAllAttachmentsFlag: (state, action: PayloadAction<boolean>) => {
      state.removeAllAttachmentsFlag = action.payload;
    },

    addMessageId: (state, action: PayloadAction<string>) => {
      const messageIdToAdd = action.payload;
      const existingIndex = state.removedMessagesId.indexOf(messageIdToAdd);

      if (existingIndex === -1) {
        return {
          ...state,
          removedMessagesId: [...state.removedMessagesId, messageIdToAdd],
        };
      } else {
        return {
          ...state,
          removedMessagesId: state.removedMessagesId.map((message, index) =>
            index === existingIndex ? messageIdToAdd : message,
          ),
        };
      }
    },
    resetMessageId: (state) => {
      state.removedMessagesId = [];
    },
    resetPiiRemovalFlags: () => {
      // Reset all state values to their initial values
      return initialState;
    },
    setAttachmentsEmptyFlag: (state, action: PayloadAction<boolean>) => {
      state.attachmentsEmptyFlag = action.payload;
    },
    setPiiRemovalAccessFlag: (state, action: PayloadAction<boolean>) => {
      state.piiRemovalAccessFlag = action.payload;
    },
    setPiiRemovedDescriptionPositions: (
      state,
      action: PayloadAction<[number, number]>,
    ) => {
      state.piiRemovedDescriptionRanges = [
        ...(state.piiRemovedDescriptionRanges ?? []),
        action.payload,
      ];
    },
    setPiiRemovedMessageRanges: (
      state,
      action: PayloadAction<{
        messageId: string;
        contentPiiRemovedRanges: [number, number][];
        messageContent: string;
        index: number;
      }>,
    ) => {
      const { messageId, contentPiiRemovedRanges, messageContent, index } =
        action.payload;

      // Update piiRemovedMessageRanges
      const existingPiiRemovedMessage = state.piiRemovedMessageRanges?.find(
        (message) => message.messageId === messageId,
      );
      if (existingPiiRemovedMessage) {
        existingPiiRemovedMessage.contentPiiRemovedRanges?.push(
          ...contentPiiRemovedRanges,
        );
      } else {
        state.piiRemovedMessageRanges?.push(action.payload);
      }

      // Update 'messages' array
      const existingMessage = state.messages?.find(
        (message) => message.messageId === messageId,
      );
      if (existingMessage) {
        existingMessage.contentPiiRemovedRanges?.push(
          ...contentPiiRemovedRanges,
        );
      } else {
        state.messages?.push({
          index,
          messageId,
          messageContent,
          contentPiiRemovedRanges,
        });
      }
    },
    clearPiiRemovedMessageRanges: (state) => {
      state.piiRemovedMessageRanges = [];
      state.messages?.forEach((message) => {
        message.contentPiiRemovedRanges = [];
      });
    },
    clearPiiRemovedDescriptionPositions: (state) => {
      state.piiRemovedDescriptionRanges = [];
    },
    clearMessageIdArray: (state) => {
      state.removedMessagesId = [];
    },
    resetMessages(state) {
      state.messages = [];
    },
    setMessages(
      state,
      action: PayloadAction<
        {
          createdAt?: string;
          name?: string;
          index: number;
          messageId: string;
          messageContent: string;
          content_pii_removed_ranges?: [number, number][];
        }[]
      >,
    ) {
      // Create a map to track unique messages by messageId
      const messageMap = new Map<string, (typeof action.payload)[0]>();

      // Add existing messages to the map
      state.messages?.forEach((message) => {
        messageMap.set(message.messageId, message);
      });

      // Iterate over the payload and add/update each message in the map
      action.payload.forEach((message) => {
        messageMap.set(message.messageId, message);
      });

      // Convert the map back to an array and assign it to state.messages
      state.messages = Array.from(messageMap.values());
    },
    setViewPiiRequest: (state, action: PayloadAction<boolean>) => {
      state.viewPiiRequest = action.payload;
    },
  },
});

export const {
  setViewPiiRequest,
  setPiiRemovalRequestData,
  setPiiRemovalRequestId,
  setSelectedMessage,
  setMessages,
  resetMessages,
  clearPiiRemovedMessageRanges,
  setPiiRemovedMessageRanges,
  setPiiRemovalFlag,
  setRemoveAllAttachmentsFlag,
  setRemoveCountryFlag,
  setRemoveReporterFlag,
  addMessageId,
  resetMessageId,
  resetPiiRemovalFlags,
  setWebSocketUrl,
  setAttachmentsEmptyFlag,
  setPiiRemovalAccessFlag,
  setPiiRemovedDescriptionPositions,
  clearPiiRemovedDescriptionPositions,
  clearMessageIdArray,
  setMessageboxId,
} = piiRemovalSlice.actions;

export default piiRemovalSlice.reducer;
